<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-brand">
        <img :src="brandLogo" alt="PreEvnt" class="auth-logo" />
        <div class="auth-brand-text">
          <span class="auth-brand-name">PreEvnt</span>
          <span class="auth-brand-sub">Admin Panel</span>
        </div>
      </div>

      <!-- loading -->
      <div v-if="loading" class="auth-state">
        <i class="fa fa-spinner fa-spin auth-state-icon" />
        <span>Loading invitation…</span>
      </div>

      <!-- invalid / expired invitation -->
      <div v-else-if="invitationError" class="auth-state error">
        <div class="auth-state-icon-circle">
          <i class="fa fa-triangle-exclamation" />
        </div>
        <h1 class="auth-title">Invitation problem</h1>
        <p class="auth-subtitle">{{ invitationError }}</p>
        <router-link to="/login" class="adm-btn-ghost auth-back-link">
          <i class="fa fa-arrow-left" /> Back to sign in
        </router-link>
      </div>

      <!-- accept form -->
      <div v-else>
        <h1 class="auth-title">Welcome aboard</h1>
        <p class="auth-subtitle">
          You've been invited to join PreEvnt as
          <span class="auth-role-pill">
            <i class="fa fa-shield-halved" />
            {{ invitation.role_name }}
          </span>
        </p>
        <p class="auth-email-line">
          <i class="fa fa-envelope" /> {{ invitation.email }}
        </p>

        <form @submit.prevent="handleSubmit" class="auth-form">
          <div class="auth-field">
            <label for="full-name" class="auth-label">Full name</label>
            <div class="auth-input-wrap">
              <i class="fa fa-user auth-input-icon" aria-hidden="true" />
              <input
                id="full-name"
                v-model="form.fullName"
                class="adm-input auth-input"
                :class="{ 'is-error': errors.fullName }"
                placeholder="Your full name"
                required
              />
            </div>
            <span v-if="errors.fullName" class="auth-error">{{ errors.fullName }}</span>
          </div>

          <div class="auth-field">
            <label for="password" class="auth-label">Password</label>
            <div class="auth-input-wrap">
              <i class="fa fa-lock auth-input-icon" aria-hidden="true" />
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="adm-input auth-input has-trailing"
                :class="{ 'is-error': errors.password }"
                placeholder="At least 8 characters"
                autocomplete="new-password"
                required
              />
              <button
                type="button"
                class="auth-input-trailing"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
              </button>
            </div>
            <span v-if="errors.password" class="auth-error">{{ errors.password }}</span>
          </div>

          <div class="auth-field">
            <label for="confirm-password" class="auth-label">Confirm password</label>
            <div class="auth-input-wrap">
              <i class="fa fa-lock auth-input-icon" aria-hidden="true" />
              <input
                id="confirm-password"
                v-model="form.confirmPassword"
                :type="showPassword ? 'text' : 'password'"
                class="adm-input auth-input"
                :class="{ 'is-error': errors.confirmPassword }"
                placeholder="Re-enter password"
                autocomplete="new-password"
                required
              />
            </div>
            <span v-if="errors.confirmPassword" class="auth-error">{{ errors.confirmPassword }}</span>
          </div>

          <button
            type="submit"
            class="adm-btn-primary auth-submit"
            :disabled="submitting"
          >
            <i v-if="submitting" class="fa fa-spinner fa-spin" />
            <i v-else class="fa fa-check" />
            {{ submitting ? "Creating account…" : "Create account" }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/utils/apiBase";
import BRAND_LOGO from "@/assets/adm-panel-logo.png";

export default {
  data() {
    return {
      brandLogo: BRAND_LOGO,
      loading: true,
      submitting: false,
      showPassword: false,
      invitationError: "",
      invitation: { email: "", role_name: "" },
      form: { fullName: "", password: "", confirmPassword: "" },
      errors: { fullName: "", password: "", confirmPassword: "" },
    };
  },
  mounted() {
    this.fetchInvitation();
  },
  methods: {
    async fetchInvitation() {
      this.loading = true;
      try {
        const res = await axios.get(
          `${API_BASE_URL}/invitations/${encodeURIComponent(this.$route.params.token)}`
        );
        this.invitation = res.data.data || {};
      } catch (err) {
        const data = err.response && err.response.data;
        const msg =
          (data && data.errors && Object.values(data.errors).flat()[0]) ||
          (data && data.message) ||
          "Invitation is invalid or expired.";
        this.invitationError = msg;
      } finally {
        this.loading = false;
      }
    },
    validateForm() {
      this.errors.fullName = this.form.fullName ? "" : "Name is required";
      this.errors.password = !this.form.password
        ? "Password is required"
        : this.form.password.length < 8
        ? "Password must be at least 8 characters"
        : "";
      this.errors.confirmPassword =
        this.form.password !== this.form.confirmPassword
          ? "Passwords do not match"
          : "";
      return !(
        this.errors.fullName ||
        this.errors.password ||
        this.errors.confirmPassword
      );
    },
    async handleSubmit() {
      if (!this.validateForm()) return;
      this.submitting = true;
      try {
        await axios.post(
          `${API_BASE_URL}/invitations/${encodeURIComponent(this.$route.params.token)}/accept`,
          {
            full_name: this.form.fullName,
            password: this.form.password,
          }
        );
        alert("Account created. Please sign in.");
        this.$router.push("/login");
      } catch (err) {
        const data = err.response && err.response.data;
        if (data && data.errors) {
          const flat = data.errors;
          this.errors.fullName = (flat.full_name && flat.full_name[0]) || "";
          this.errors.password = (flat.password && flat.password[0]) || "";
          if (flat.token) {
            this.invitationError = flat.token[0] || "Invitation invalid.";
          }
          if (flat.email) {
            alert(flat.email[0]);
          }
        } else {
          alert(`Failed: ${err.message || "unknown error"}`);
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6fa;
  padding: 1.5rem;
}

.auth-card {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 14px;
  padding: 2rem;
  box-shadow: 0 10px 32px rgba(16, 24, 40, 0.08);
}

.auth-brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}
.auth-logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: contain;
}
.auth-brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.auth-brand-name {
  font-weight: 800;
  font-size: 1rem;
  color: #1f2540;
}
.auth-brand-sub {
  font-size: 0.7rem;
  color: #98a0ad;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.auth-title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  color: #1f2540;
}
.auth-subtitle {
  margin: 0.25rem 0 0.5rem;
  font-size: 0.92rem;
  color: #6b7383;
}
.auth-email-line {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.7rem;
  background: #f7f8fb;
  border: 1px solid #e6e8ee;
  border-radius: 999px;
  color: #1f2540;
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  margin-bottom: 1.25rem;
}

.auth-role-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 2px 10px;
  background: #eef1f7;
  color: #2a345d;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  vertical-align: middle;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}
.auth-field {
  display: flex;
  flex-direction: column;
}
.auth-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6577;
  margin-bottom: 0.35rem;
}

.auth-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.auth-input-icon {
  position: absolute;
  left: 0.85rem;
  color: #98a0ad;
  font-size: 0.85rem;
  pointer-events: none;
}
.auth-input {
  width: 100%;
  padding-left: 2.25rem;
}
.auth-input.has-trailing {
  padding-right: 2.5rem;
}
.auth-input.is-error,
.auth-input.is-error:focus {
  border-color: #b02a37;
  box-shadow: 0 0 0 3px rgba(176, 42, 55, 0.15);
}
.auth-input-trailing {
  position: absolute;
  right: 0.35rem;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: transparent;
  border: none;
  color: #5b6577;
  cursor: pointer;
}
.auth-input-trailing:hover {
  background: #eef0f4;
  color: #2a345d;
}
.auth-error {
  color: #b02a37;
  font-size: 0.78rem;
  margin-top: 0.3rem;
}

.auth-submit {
  width: 100%;
  justify-content: center;
  margin-top: 0.5rem;
}

/* loading / error state */
.auth-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 0;
  color: #5b6577;
  font-size: 0.9rem;
  text-align: center;
}
.auth-state-icon {
  color: #2a345d;
  font-size: 1.6rem;
}
.auth-state-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #fdecee;
  color: #b02a37;
  font-size: 1.6rem;
  margin-bottom: 0.5rem;
}
.auth-state.error .auth-title {
  margin-bottom: 0.25rem;
}
.auth-state.error .auth-subtitle {
  margin: 0 0 1rem;
}
.auth-back-link {
  margin-top: 0.5rem;
  text-decoration: none;
}
</style>
